import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';

/** One stop on a guided tour. `target` is a `data-tour` value on the page. */
export interface TourStep {
  target: string;
  title: string;
  body: string;
  /** Hint the host page can use to show the right view before this step renders. */
  context?: string;
  placement?: 'bottom' | 'top' | 'right' | 'left';
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Lightweight step-by-step tour: a spotlight around the current target and a
 * card with the explanation. No library — the app's pages are simple enough
 * that a dozen lines of geometry beat a dependency. Host pages mark anchors
 * with `data-tour="…"` and pass the steps; `stepChange` fires before a step
 * renders so the host can switch tabs or views.
 */
@Component({
  selector: 'fnf-tour',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tour.component.html',
  styleUrl: './tour.component.css',
})
export class TourComponent implements AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  @Input({ required: true }) steps: TourStep[] = [];
  @Input() startAt = 0;
  @Output() stepChange = new EventEmitter<TourStep>();
  @Output() closed = new EventEmitter<'done' | 'skipped'>();

  index = 0;
  spot: Rect | null = null;
  card: { top: number; left: number } = { top: 0, left: 0 };
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly pad = 8;

  get step(): TourStep {
    return this.steps[this.index];
  }

  get isLast(): boolean {
    return this.index >= this.steps.length - 1;
  }

  ngAfterViewInit(): void {
    this.index = Math.min(Math.max(this.startAt, 0), Math.max(this.steps.length - 1, 0));
    this.show();
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  reposition(): void {
    this.measure();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.skip();
    } else if (event.key === 'ArrowRight' || event.key === 'Enter') {
      this.next();
    } else if (event.key === 'ArrowLeft') {
      this.back();
    }
  }

  next(): void {
    if (this.isLast) {
      this.closed.emit('done');
      return;
    }
    this.index++;
    this.show();
  }

  back(): void {
    if (this.index === 0) {
      return;
    }
    this.index--;
    this.show();
  }

  skip(): void {
    this.closed.emit('skipped');
  }

  /**
   * Let the host react (switch view), then measure once the DOM has settled.
   * A zone-patched timer is used rather than requestAnimationFrame so change
   * detection is guaranteed to run for this component afterwards.
   */
  private show(): void {
    if (!this.step) {
      return;
    }
    this.stepChange.emit(this.step);
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => this.measure(true), 60);
  }

  private measure(scrollIntoView = false): void {
    if (!this.step) {
      return;
    }
    const el = document.querySelector<HTMLElement>(`[data-tour="${this.step.target}"]`);
    if (!el) {
      // Anchor not on screen for this state (e.g. no reservation selected):
      // centre the card and drop the spotlight.
      this.spot = null;
      this.card = {
        top: Math.max(24, window.innerHeight / 2 - 120),
        left: Math.max(16, window.innerWidth / 2 - 180),
      };
      this.cdr.detectChanges();
      return;
    }
    if (scrollIntoView) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    const r = el.getBoundingClientRect();
    this.spot = {
      top: r.top - this.pad,
      left: r.left - this.pad,
      width: r.width + this.pad * 2,
      height: r.height + this.pad * 2,
    };
    this.card = this.placeCard(this.spot);
    this.cdr.detectChanges();
  }

  private placeCard(spot: Rect): { top: number; left: number } {
    const cardW = Math.min(360, window.innerWidth - 32);
    const cardH = 190;
    const gap = 12;
    const want =
      this.step.placement ??
      (spot.top + spot.height + gap + cardH < window.innerHeight ? 'bottom' : 'top');
    let top: number;
    let left = spot.left;
    switch (want) {
      case 'top':
        top = spot.top - gap - cardH;
        break;
      case 'right':
        top = spot.top;
        left = spot.left + spot.width + gap;
        break;
      case 'left':
        top = spot.top;
        left = spot.left - gap - cardW;
        break;
      default:
        top = spot.top + spot.height + gap;
    }
    left = Math.min(Math.max(16, left), window.innerWidth - cardW - 16);
    top = Math.min(Math.max(16, top), window.innerHeight - cardH - 16);
    return { top, left };
  }
}
